// LA ANATOMÍA DE UN BLOQUE — y nadie se sale de ella.
//
// La pantalla actual tiene 35 gráficos con 35 formas distintas de presentarse.
// Acá todos los bloques tienen la misma, y el orden importa:
//
//   TITULAR    la conclusión, no la categoría.
//              "Los sábados te dejan casi el doble que un martes",
//              no "Ingreso por día de la semana".
//   frase      qué mirar y qué hacer. Una o dos líneas.
//   rango      SIEMPRE el rango real de datos: "26/07 – 24/08 · 27 días con
//              movimiento". Sin esto, "30d" puede ser 35 días de calendario y
//              nadie se entera.
//   [prueba]   el gráfico o la tabla. Va DEBAJO, porque es la evidencia del
//              titular y no al revés.
//   acciones   copiar, descargar, ver el detalle.
//
// La regla que hace que esto funcione: **si un bloque no puede generar una
// frase, el bloque no va.** Un gráfico que no permite concluir nada es un
// gráfico que el dueño va a mirar sin saber qué hacer con él.

import React, { useState } from "react";
import { Card, CardBody } from "reactstrap";
import { UI, ANCHO_LECTURA } from "./theme";

/**
 * Mayúscula inicial, y sólo la inicial.
 *
 * Los titulares se arman con datos: "agosto va a terminar arriba del mes
 * pasado". En español los meses van en minúscula —y así tienen que quedar
 * cuando caen en el medio de la frase, como en "Vas a terminar agosto cerca
 * de..."— pero al principio de una oración va mayúscula igual que cualquier
 * palabra. Por eso se resuelve acá, en la anatomía común, y no en cada bloque:
 * el que escribe el titular no tiene que acordarse.
 *
 * `text-transform: capitalize` NO sirve: pone en mayúscula todas las palabras.
 */
const mayusculaInicial = (v) =>
  typeof v === "string" && v.length ? v.charAt(0).toUpperCase() + v.slice(1) : v;

const Bloque = ({
  id,
  nombre,             // rótulo fijo; ver la nota de abajo
  titular,
  frase,
  rango,
  acciones = null,
  detalle = null,      // contenido plegado; si viene, aparece "Ver el detalle"
  detalleLabel = "Ver el detalle",
  acento = null,       // barra de color a la izquierda
  children,
}) => {
  const [abierto, setAbierto] = useState(false);

  return (
    <Card className="shadow-sm mb-3" id={id}>
      <CardBody style={acento ? { borderLeft: `4px solid ${acento}` } : undefined}>
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            {/* EL RÓTULO FIJO, arriba del titular.
                El titular se arma con los datos, así que cambia todos los días:
                eso se lee muy bien, pero le saca el NOMBRE al bloque y deja de
                ser un lugar al que se vuelve. Paso real: el usuario buscó "Lo
                que salta a la vista" y no lo encontró, porque ese día el bloque
                se titulaba "Cada cochera te deja $3.603 por día".
                Chico y gris: orienta sin competir con la conclusión. */}
            {nombre && (
              <div style={{
                fontSize: 11, color: UI.textoSuave, textTransform: "uppercase",
                letterSpacing: 0.6, fontWeight: 600, marginBottom: 3,
              }}>
                {nombre}
              </div>
            )}
            <h5 style={{ fontSize: 16, fontWeight: 700, color: UI.texto, margin: 0, lineHeight: 1.3 }}>
              {mayusculaInicial(titular)}
            </h5>
            {frase && (
              <p style={{ fontSize: 13.5, color: UI.textoSuave, margin: "4px 0 0", lineHeight: 1.45, maxWidth: ANCHO_LECTURA }}>
                {frase}
              </p>
            )}
          </div>
          {acciones && <div style={{ flexShrink: 0 }}>{acciones}</div>}
        </div>

        {/* El rango va arriba de la prueba, no al pie: es la condición para
            leerla, no una nota al margen. */}
        {rango && (
          <div style={{ fontSize: 11.5, color: UI.textoSuave, marginTop: 8 }}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
              date_range
            </span>
            {rango}
          </div>
        )}

        {children && <div style={{ marginTop: 14 }}>{children}</div>}

        {detalle && (
          <>
            <button
              type="button"
              onClick={() => setAbierto((v) => !v)}
              className="btn btn-link p-0 mt-3"
              style={{ fontSize: 13, textDecoration: "none" }}
              aria-expanded={abierto}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
                {abierto ? "expand_less" : "expand_more"}
              </span>
              {abierto ? "Ocultar el detalle" : detalleLabel}
            </button>
            {abierto && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${UI.linea}` }}>
                {detalle}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default Bloque;
